import { useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';

import { AdminButton } from '@/admin/components/AdminButton';
import styles from '@/admin/components/AdminForceDeleteModal.module.scss';
import { AdminModal } from '@/admin/components/AdminModal';

type AdminForceDeleteModalProps = {
  /** `vessel` or `service provider`, as it reads in a sentence. */
  kind: string;
  name: string;
  /** Who created it, for the warning; null when that isn't known. */
  creatorName: string | null;
  /** False only for records the platform itself created. */
  userCreated: boolean;
  /** What goes with it, one sentence. */
  consequences: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
};

/**
 * Confirms an admin's delete of something they don't own. It can't be undone, so the name has
 * to be typed before Delete unlocks.
 */
export const AdminForceDeleteModal = ({
  kind,
  name,
  creatorName,
  userCreated,
  consequences,
  onClose,
  onConfirm,
}: AdminForceDeleteModalProps) => {
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const matches = typed.trim() === name.trim();

  const confirm = async () => {
    if (!matches) return;

    setError('');
    setDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      console.error(err);
      setError(`Couldn’t delete the ${kind}. Please try again.`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminModal
      title={`Delete “${name}”?`}
      onClose={onClose}
      footer={
        <>
          <AdminButton onClick={onClose} disabled={deleting}>
            Cancel
          </AdminButton>
          <AdminButton tone="danger" onClick={confirm} disabled={!matches || deleting}>
            Delete
          </AdminButton>
        </>
      }
    >
      {userCreated && (
        <div className={styles.warning} role="alert">
          <FiAlertTriangle className={styles.icon} aria-hidden />
          <p>
            This {kind} was created by {creatorName ?? 'a user'}, not by VesselHaven. Deleting it
            takes it away from them and everyone on it.
          </p>
        </div>
      )}

      <p className={styles.text}>{consequences} This can’t be undone.</p>

      <label className={styles.field}>
        <span className={styles.label}>Please type “{name}” to confirm.</span>
        <input
          autoFocus
          className={styles.input}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && confirm()}
        />
      </label>

      {error && <p className={styles.error}>{error}</p>}
    </AdminModal>
  );
};
